import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import { useDeckStore } from '../../store/deckStore';
import { useDepartmentName } from '../../store/departmentStore';
import { navigate, paths } from '../../lib/router';
import { PortalHeader } from '../home/PortalHeader';
import { SlideThumbnail } from '../SlideThumbnail';
import { PresentMode } from '../PresentMode';
import { ExportFrames } from '../Canvas';
import { DeckComments } from './DeckComments';

/** Read-only page for a deck: browse slides, present, download, copy, or edit when it's yours. */
export function DeckViewer({ id }: { id: string }) {
  const deckId = useDeckStore((s) => s.deckId);
  const title = useDeckStore((s) => s.title);
  const slides = useDeckStore((s) => s.slides);
  const ownerName = useDeckStore((s) => s.ownerName);
  const category = useDeckStore((s) => s.category);
  const visibility = useDeckStore((s) => s.visibility);
  const canEdit = useDeckStore((s) => s.canEdit);
  const active = useDeckStore((s) => s.activeSlideIndex);
  const presenting = useDeckStore((s) => s.presenting);
  const openDeck = useDeckStore((s) => s.openDeck);
  const setActiveSlide = useDeckStore((s) => s.setActiveSlide);
  const setPresenting = useDeckStore((s) => s.setPresenting);
  const duplicateDeck = useDeckStore((s) => s.duplicateDeck);
  const categoryName = useDepartmentName();

  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [downloadAnchor, setDownloadAnchor] = useState<HTMLElement | null>(null);
  const [busy, setBusy] = useState(false);
  const stageRef = useRef<HTMLDivElement>(null);
  const [stage, setStage] = useState({ w: 0, h: 0 });

  useEffect(() => {
    let cancelled = false;
    openDeck(id)
      .then(() => !cancelled && setStatus('ready'))
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Could not open this deck');
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [id, openDeck]);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setStage({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    return () => ro.disconnect();
  }, [status]);

  useEffect(() => {
    if (presenting || status !== 'ready') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') setActiveSlide(Math.min(active + 1, slides.length - 1));
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') setActiveSlide(Math.max(active - 1, 0));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [presenting, status, active, slides.length, setActiveSlide]);

  const download = async (kind: 'pptx' | 'pdf') => {
    setDownloadAnchor(null);
    setBusy(true);
    try {
      if (kind === 'pptx') {
        const { exportPptx } = await import('../../lib/exportPptx');
        await exportPptx({ title, slides });
      } else {
        const { exportPdf } = await import('../../lib/exportPdf');
        await exportPdf({ title });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Download failed');
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    setBusy(true);
    try {
      navigate(paths.edit(await duplicateDeck(id)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not copy this deck');
      setBusy(false);
    }
  };

  const ready = status === 'ready' && deckId === id;
  const slide = slides[active];
  const slideW = Math.max(0, Math.min(stage.w - 32, ((stage.h - 32) * 16) / 9));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: 'background.default' }}>
      <PortalHeader>
        <Tooltip title="Back to home">
          <IconButton aria-label="Back to home" onClick={() => navigate(paths.home())}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {ready && (
          <>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" component="h1" noWrap>
                {title}
              </Typography>
              <Typography variant="caption" noWrap component="div">
                {ownerName} · {slides.length} slides
              </Typography>
            </Box>
            <Chip size="small" label={categoryName(category)} sx={{ height: 22 }} />
            <Box sx={{ flex: 1 }} />
            <Stack direction="row" spacing={0.75} sx={{ flexShrink: 0 }}>
              <Button color="neutral" startIcon={<ContentCopyOutlinedIcon />} disabled={busy} onClick={() => void copy()}>
                Make a copy
              </Button>
              <Button
                variant="outlined"
                color="neutral"
                disabled={busy}
                startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <FileDownloadOutlinedIcon />}
                endIcon={<KeyboardArrowDownIcon />}
                onClick={(e) => setDownloadAnchor(e.currentTarget)}
              >
                Download
              </Button>
              <Menu
                anchorEl={downloadAnchor}
                open={Boolean(downloadAnchor)}
                onClose={() => setDownloadAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem onClick={() => void download('pptx')}>
                  <ListItemText primary="PowerPoint" secondary=".pptx" />
                </MenuItem>
                <MenuItem onClick={() => void download('pdf')}>
                  <ListItemText primary="PDF" secondary=".pdf" />
                </MenuItem>
              </Menu>
              {canEdit && (
                <Button variant="outlined" color="neutral" startIcon={<EditOutlinedIcon />} onClick={() => navigate(paths.edit(id))}>
                  Edit
                </Button>
              )}
              <Button variant="contained" startIcon={<PlayArrowOutlinedIcon />} onClick={() => setPresenting(true)}>
                Present
              </Button>
            </Stack>
          </>
        )}
      </PortalHeader>

      {status === 'error' && (
        <Box sx={{ maxWidth: 520, mx: 'auto', mt: 8, px: 2, textAlign: 'center' }}>
          <Alert severity="warning" sx={{ textAlign: 'left', mb: 2 }}>
            {error === 'Deck not found'
              ? "This deck isn't available. It may have been deleted or made private by its owner."
              : error}
          </Alert>
          <Button variant="contained" onClick={() => navigate(paths.home())}>
            Back to home
          </Button>
        </Box>
      )}

      {status !== 'error' && (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
          <Box
            component="nav"
            aria-label="Slides"
            sx={{
              width: 200,
              flexShrink: 0,
              overflowY: 'auto',
              p: 2,
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column',
              gap: 1.5,
              bgcolor: 'background.paper',
              borderRight: 1,
              borderColor: 'divider',
            }}
          >
            {ready &&
              slides.map((s, i) => (
                <Stack key={s.id} direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
                  <Typography
                    variant="caption"
                    sx={{ width: 14, textAlign: 'right', fontWeight: 500, color: i === active ? 'primary.main' : 'text.disabled' }}
                  >
                    {i + 1}
                  </Typography>
                  <ButtonBase
                    aria-label={`Slide ${i + 1}`}
                    aria-current={i === active ? 'true' : undefined}
                    onClick={() => setActiveSlide(i)}
                    sx={(t) => ({
                      flex: 1,
                      display: 'block',
                      borderRadius: 1,
                      overflow: 'hidden',
                      border: 1,
                      borderColor: 'divider',
                      boxShadow: i === active ? `0 0 0 2px ${t.palette.primary.main}` : 'none',
                    })}
                  >
                    <SlideThumbnail slide={s} />
                  </ButtonBase>
                </Stack>
              ))}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <Box ref={stageRef} sx={{ flex: 1, minHeight: 0, display: 'grid', placeItems: 'center' }}>
              {!ready ? (
                <CircularProgress size={28} aria-label="Loading deck" />
              ) : (
                slide && (
                  <Box
                    sx={{ width: slideW, boxShadow: '0 1px 3px rgba(16,24,40,0.1), 0 12px 32px -8px rgba(16,24,40,0.18)' }}
                  >
                    <SlideThumbnail slide={slide} />
                  </Box>
                )
              )}
            </Box>
            {ready && (
              <Stack direction="row" spacing={1} sx={{ justifyContent: 'center', alignItems: 'center', py: 1.5 }}>
                <IconButton aria-label="Previous slide" disabled={active === 0} onClick={() => setActiveSlide(active - 1)}>
                  <ChevronLeftIcon />
                </IconButton>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 64, textAlign: 'center' }}>
                  {active + 1} / {slides.length}
                </Typography>
                <IconButton
                  aria-label="Next slide"
                  disabled={active >= slides.length - 1}
                  onClick={() => setActiveSlide(active + 1)}
                >
                  <ChevronRightIcon />
                </IconButton>
              </Stack>
            )}
          </Box>
          {ready && (
            <DeckComments
              deckId={id}
              canComment={visibility === 'company' || visibility === 'unlisted'}
              isOwner={canEdit}
            />
          )}
        </Box>
      )}

      {ready && (
        <>
          <PresentMode />
          <ExportFrames />
        </>
      )}
    </Box>
  );
}
